import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { ConfirmationModal } from "../../../components/ConfirmModal";
import { Container } from "../../../components/Container";
import { CustomButton, IButtonVariant } from "../../../components/CustomButton";
import { showCustomToast } from "../../../components/CustomToast";
import { useModal } from "../../../hooks/useModal";
import {
  closeBillingThunk,
  putUpdateBillingThunk,
  removeBillingExpense,
  updateBillingRealWasteAmount,
  upsertBillingExpense,
} from "../../../reducers/billingReducer";
import {
  getBillingIsUpdating,
  getBillingRealExpensePerKg,
  getBillingRealExpenses,
  getBillingRealExpensesValueSum,
  getBillingRealWasteAmount,
  getCanSaveBillingBilling,
  getIsBillingInActiveStatus,
} from "../../../selectors/billing";
import { useAppDispatch } from "../../../store";
import { BillingLocationsTableType } from "../../../types/billingLocations";
import { sanitizePositiveNumberInput } from "../../../utils/input";
import { BillingBillingStatCards } from "../BillingBillingStatCards";
import { BillingExpenses } from "../BillingExpenses/BillingExpenses";
import { BillingLocationsTableContainer } from "../BillingLocationsTable/BillingLocationsTableContainer";
import { BillingWasteWeightInput } from "../BillingWasteWeightInput/BillingWasteWeightInput";

import styles from "./BillingPageBilling.module.scss";

export const BillingPageBilling: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const realExpenses = useSelector(getBillingRealExpenses);
  const realWasteAmount = useSelector(getBillingRealWasteAmount);
  const realExpensesValueSum = useSelector(getBillingRealExpensesValueSum);
  const realExpensePerKg = useSelector(getBillingRealExpensePerKg);
  const isBillingInActive = useSelector(getIsBillingInActiveStatus);
  const isUpdating = useSelector(getBillingIsUpdating);
  const canSaveBillingBilling = useSelector(getCanSaveBillingBilling);

  const { isModalVisible, toggleModalVisibility } = useModal();

  const handleSave = async () => {
    const result = await dispatch(putUpdateBillingThunk());

    if (putUpdateBillingThunk.fulfilled.match(result)) {
      showCustomToast(t("billingPageBilling.saveSuccess"), { type: "success" });
    } else if (putUpdateBillingThunk.rejected.match(result)) {
      showCustomToast(t(`billingApiErrors.${result.payload}`), { type: "error" });
    }
  };

  const handleSaveAndFinish = async () => {
    const putResult = await dispatch(putUpdateBillingThunk());
    if (putUpdateBillingThunk.rejected.match(putResult)) {
      showCustomToast(t(`billingApiErrors.${putResult.payload}`), { type: "error" });
      return;
    }

    const closeResult = await dispatch(closeBillingThunk());
    if (closeBillingThunk.rejected.match(closeResult)) {
      showCustomToast(t(`billingApiErrors.${closeResult.payload}`), { type: "error" });
      return;
    }
    showCustomToast(t("billingPageBilling.saveAndCloseSuccess"), { type: "success" });
  };

  const isDisabled = !isBillingInActive || isUpdating;

  return (
    <>
      <Container>
        <BillingWasteWeightInput
          label={t("billingPageBilling.expectedWasteWeight")}
          value={realWasteAmount}
          onChange={(newValue) => {
            dispatch(updateBillingRealWasteAmount(sanitizePositiveNumberInput(newValue)));
          }}
          isDisabled={isDisabled}
        />
      </Container>
      <BillingExpenses
        label={t("billingPageBilling.expectedExpensesForWaste")}
        totalLabel={t("billingPageBilling.totalLabel")}
        expenses={realExpenses}
        expensesValueSum={realExpensesValueSum}
        costPerKg={realExpensePerKg}
        onCreate={() => {
          dispatch(upsertBillingExpense({ real: true }));
        }}
        onChange={(expense) => {
          dispatch(upsertBillingExpense(expense));
        }}
        onRemove={(id) => {
          dispatch(removeBillingExpense(id));
        }}
        isDisabled={isDisabled}
      />
      <BillingBillingStatCards />
      <BillingLocationsTableContainer showFeePercentageCollected tableType={BillingLocationsTableType.Billing} />
      <div className={styles.buttonContainer}>
        {isBillingInActive && (
          <>
            <CustomButton
              onClick={toggleModalVisibility}
              text={t("billingPageBilling.saveAndClose")}
              className={styles.saveBtn}
              disabled={isUpdating || !canSaveBillingBilling}
              variant={IButtonVariant.white}
            />
            <CustomButton
              onClick={handleSave}
              text={t("billingPageBilling.save")}
              className={styles.saveBtn}
              disabled={isUpdating || !canSaveBillingBilling}
            />
          </>
        )}
      </div>
      <ConfirmationModal
        title={t("billingPageBilling.confirmTitle")}
        text={t("billingPageBilling.confirmText")}
        isModalVisible={isModalVisible}
        toggleModalVisibility={toggleModalVisibility}
        onSuccess={handleSaveAndFinish}
      />
    </>
  );
};
